import { ScenarioDisplayType } from '@/orval/generated/user/model/scenarioDisplayType'

/**
 * 表示タイプの性質を問う述語。
 *
 * `displayType === 'POPUP'` を各所に直書きすると、表示タイプを追加するたびに漏れが
 * 実行時バグになる（型で守られないため）。「何型か」ではなく「どういう性質か」で
 * 判定できるよう、ここに集約する。
 *
 * バックエンドの `src/commons/displayType.ts` と対応させること。
 */

/**
 * ノードベース型か（動画を ScenarioNode として持つ）。
 *
 * POPUP / BOX / WIDE は React Flow エディタで分岐グラフを編集する。
 * WIDE_SINGLE はノードを1つだけ持ち、専用の簡易エディタで編集する（分岐グラフは出さない）。
 */
export function isNodeBasedDisplayType(displayType: ScenarioDisplayType | string | undefined): boolean {
  return (
    displayType === 'POPUP' ||
    displayType === 'BOX' ||
    displayType === 'WIDE' ||
    displayType === 'WIDE_SINGLE'
  )
}

/**
 * シナリオ単位の入口サムネイルを持つか（POPUP=右下の円形／BOX=ページ内バナー）。
 */
export function hasEntryThumbnail(displayType: ScenarioDisplayType | string | undefined): boolean {
  return displayType === 'POPUP' || displayType === 'BOX'
}

/**
 * 公開プレイリストページ（`/company/{tenantId}/playlist/{scenarioId}`）を持つか。
 *
 * 分岐型（POPUP/BOX/WIDE）は script 埋め込み専用で公開URLを持たない。
 */
export function hasPublicPlaylistPage(displayType: ScenarioDisplayType | string | undefined): boolean {
  return displayType === 'SLIDER' || displayType === 'TILE'
}

/**
 * 横型動画（16:9）専用の表示タイプか。WIDE / WIDE_SINGLE。
 */
export function requiresLandscapeVideo(displayType: ScenarioDisplayType | string | undefined): boolean {
  return displayType === 'WIDE' || displayType === 'WIDE_SINGLE'
}

/**
 * 縦型動画専用の表示タイプか。POPUP と BOX（縦長の枠で再生するため、横型は左右が見切れる）。
 */
export function requiresPortraitVideo(displayType: ScenarioDisplayType | string | undefined): boolean {
  return displayType === 'POPUP' || displayType === 'BOX'
}

/**
 * 埋め込み先ページのレイアウトに沿って配置するか（インライン）。
 *
 * SLIDER/TILE は一覧をその場に描画し、BOX は入口バナーを、WIDE / WIDE_SINGLE はポスターを
 * その場に置く（再生はポスターから開くモーダル。ADR-0021）。いずれも shadowHost を `position: fixed` に
 * してはいけない。POPUP だけが画面隅に切り離された固定オーバーレイ。
 */
export function isInlineDisplayType(displayType: ScenarioDisplayType | string | undefined): boolean {
  return (
    displayType === 'SLIDER' ||
    displayType === 'TILE' ||
    displayType === 'BOX' ||
    displayType === 'WIDE' ||
    displayType === 'WIDE_SINGLE'
  )
}
